<template>
  <DefaultHeader
    title="Фриланс"
    subtitle="Фриланс-предложения о работе"
    class="fixed-header"
    :offset="650"
  />

  <div
    class="page"
    :class="{
      light: isLightTheme,
      dark: !isLightTheme
    }"
  >

    <section class="hero">
      <div class="hero-content">
        <div class="hero-card">
          <div class="hero-copy">
            <div class="hero-eyebrow">
              <span class="hero-eyebrow-icon">
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  aria-hidden="true"
                >
                  <path
                    d="M5 7.5H19M7.5 4.5V7.5M16.5 4.5V7.5M6.5 7.5H17.5C18.6 7.5 19.5 8.4 19.5 9.5V17.5C19.5 18.6 18.6 19.5 17.5 19.5H6.5C5.4 19.5 4.5 18.6 4.5 17.5V9.5C4.5 8.4 5.4 7.5 6.5 7.5Z"
                    stroke="currentColor"
                    stroke-width="1.6"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
              </span>

              <span>
                Подборка возможностей
              </span>
            </div>

            <h1 class="hero-title">
              Фриланс-посты
            </h1>

            <p class="hero-subtitle">
              Собираем предложения из Telegram-каналов
              и оставляем только полезное — без лишнего шума.
            </p>
          </div>

          <div class="hero-meta">
            <div class="hero-meta-title">
              <span class="hero-meta-dot" />
              Темы определяет AI
            </div>

            <div class="themes">
              <span class="theme-pill theme-design">
                <span class="theme-dot" />
                Дизайн
              </span>

              <span class="theme-pill theme-copywriting">
                <span class="theme-dot" />
                Копирайтинг
              </span>

              <span class="theme-pill theme-marketing">
                <span class="theme-dot" />
                Маркетинг
              </span>

              <span class="theme-pill theme-video">
                <span class="theme-dot" />
                Видео
              </span>

              <span class="theme-pill theme-other">
                <span class="theme-dot" />
                Прочее
              </span>
            </div>

            <div class="contacts-row">
              <span class="contacts-icon">
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  aria-hidden="true"
                >
                  <path
                    d="M4.5 6.5C4.5 5.4 5.4 4.5 6.5 4.5H17.5C18.6 4.5 19.5 5.4 19.5 6.5V17.5C19.5 18.6 18.6 19.5 17.5 19.5H6.5C5.4 19.5 4.5 18.6 4.5 17.5V6.5Z"
                    stroke="currentColor"
                    stroke-width="1.5"
                  />

                  <path
                    d="M5.5 7L12 12L18.5 7"
                    stroke="currentColor"
                    stroke-width="1.5"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
              </span>

              <span class="contacts-label">
                Контакты внутри постов:
              </span>

              <span>Telegram</span>
              <span class="contacts-separator">•</span>
              <span>Email</span>
              <span class="contacts-separator">•</span>
              <span>Ссылки</span>
            </div>
          </div>

          <div
            class="hero-decoration"
            aria-hidden="true"
          >
            <div class="hero-decoration-circle" />
            <div class="hero-decoration-circle hero-decoration-circle-small" />
          </div>
        </div>
      </div>
    </section>

    <!-- ===================================================== -->
    <!-- CONTENT -->
    <!-- ===================================================== -->

    <main class="freelance-wrapper">
      <div class="content-heading">
        <div>
          <h2 class="content-title">
            Свежие предложения
          </h2>

          <p class="content-subtitle">
            Открывайте карточку, чтобы посмотреть пост полностью
          </p>
        </div>

        <span
          v-if="!isLoading && freelances.length"
          class="posts-count"
        >
          {{ freelances.length }}
        </span>
      </div>

      <div
        ref="gridRef"
        class="freelance-grid"
        :class="{
          loading: isLoading,
          empty: !isLoading && freelances.length === 0
        }"
      >
        <!-- SKELETONS -->

        <template v-if="isLoading">
          <div
            v-for="index in 8"
            :key="`skeleton-${index}`"
            class="masonry-item"
          >
            <FreelanceCardSkeleton />
          </div>
        </template>

        <!-- CARDS -->

        <template v-else-if="freelances.length > 0">
          <div
            v-for="post in freelances"
            :key="post.id"
            class="masonry-item"
          >
            <FreelanceCard
              :post="post"
              @click="openFreelance(post)"
            />
          </div>
        </template>

        <!-- EMPTY -->

        <div
          v-else
          class="empty-state"
        >
          <div class="empty-icon">
            <svg
              viewBox="0 0 24 24"
              fill="none"
              aria-hidden="true"
            >
              <path
                d="M5 7.5H19M7.5 4.5V7.5M16.5 4.5V7.5M6.5 7.5H17.5C18.6 7.5 19.5 8.4 19.5 9.5V17.5C19.5 18.6 18.6 19.5 17.5 19.5H6.5C5.4 19.5 4.5 18.6 4.5 17.5V9.5C4.5 8.4 5.4 7.5 6.5 7.5Z"
                stroke="currentColor"
                stroke-width="1.6"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </div>

          <h3 class="empty-title">
            Пока ничего нового
          </h3>

          <p class="empty-text">
            Подборка обновляется автоматически.
            Загляните сюда немного позже.
          </p>
        </div>
      </div>
    </main>

    <Transition name="fade">
      <div
        v-if="isDrawerOpen"
        class="drawer-backdrop"
        @click="closeFreelance"
      />
    </Transition>
  </div>

  <Footer class="fixed-footer" />

  <FreelanceDetailDrawer
    v-if="isDrawerOpen"
    :freelance="selectedFreelance"
    @close="closeFreelance"
  />
</template>

<script setup>
import {
  computed,
  nextTick,
  onBeforeUnmount,
  onMounted,
  ref,
  watch
} from "vue";

import {
  getAuth,
  onAuthStateChanged
} from "firebase/auth";

import DefaultHeader from "../components/FreelanceHeader.vue";
import Footer from "../components/MiniFooter.vue";
import FreelanceCard from "../components/FreelanceCard.vue";
import FreelanceCardSkeleton from "../components/FreelanceCardSkeleton.vue";
import FreelanceDetailDrawer from "./FreelanceDetailDrawer.vue";

import FreelanceService from "../api/FreelanceService.js";

const freelances = ref([]);
const isLoading = ref(true);

const isDrawerOpen = ref(false);
const selectedFreelance = ref(null);

const gridRef = ref(null);

const currentTheme = ref(
  document.documentElement.getAttribute("data-theme") || "light"
);

const isLightTheme = computed(() => {
  return currentTheme.value !== "dark";
});

let authUnsubscribe = null;
let resizeObserver = null;
let themeObserver = null;
let resizeFrame = null;

/* ========================================================= */
/* MASONRY */
/* ========================================================= */

function getMasonryItems() {
  if (!gridRef.value) {
    return [];
  }

  return Array.from(
    gridRef.value.querySelectorAll(".masonry-item")
  );
}

function calculateMasonryItem(item) {
  const grid = gridRef.value;

  if (!grid || !item) {
    return;
  }

  if (
    window.matchMedia(
      "(max-width: 680px)"
    ).matches
  ) {
    item.style.gridRowEnd = "auto";
    return;
  }

  const gridStyle =
    window.getComputedStyle(grid);

  const rowHeight =
    Number.parseFloat(
      gridStyle.getPropertyValue(
        "grid-auto-rows"
      )
    );

  const rowGap =
    Number.parseFloat(
      gridStyle.getPropertyValue(
        "row-gap"
      )
    );

  if (
    !Number.isFinite(rowHeight) ||
    rowHeight <= 0
  ) {
    return;
  }

  const itemHeight =
    item.getBoundingClientRect().height;

  const safeGap =
    Number.isFinite(rowGap)
      ? rowGap
      : 0;

  const rowSpan =
    Math.ceil(
      (itemHeight + safeGap) /
      (rowHeight + safeGap)
    );

  item.style.gridRowEnd =
    `span ${Math.max(rowSpan, 1)}`;
}

async function updateMasonry() {
  await nextTick();

  const items =
    getMasonryItems();

  if (items.length === 0) {
    return;
  }

  items.forEach(item => {
    item.style.gridRowEnd =
      "auto";
  });

  if (resizeFrame) {
    cancelAnimationFrame(
      resizeFrame
    );
  }

  resizeFrame =
    requestAnimationFrame(
      () => {
        items.forEach(item => {
          calculateMasonryItem(
            item
          );
        });
      }
    );
}

async function observeMasonryItems() {
  await nextTick();

  resizeObserver?.disconnect();

  resizeObserver =
    new ResizeObserver(
      entries => {
        entries.forEach(entry => {
          calculateMasonryItem(
            entry.target
          );
        });
      }
    );

  getMasonryItems()
    .forEach(item => {
      resizeObserver.observe(
        item
      );
    });

  await updateMasonry();
}

/* ========================================================= */
/* DATA */
/* ========================================================= */

async function loadFreelances() {
  isLoading.value = true;

  try {
    const data =
      await FreelanceService
        .getFreelancePosts();

    freelances.value =
      Array.isArray(data)
        ? data
        : [];
  } catch (error) {
    console.error(
      "❌ Ошибка загрузки фриланс-постов:",
      error
    );

    freelances.value = [];
  } finally {
    isLoading.value = false;
  }
}

/* ========================================================= */
/* DRAWER */
/* ========================================================= */

function openFreelance(post) {
  selectedFreelance.value =
    post;

  isDrawerOpen.value =
    true;

  document.body.style.overflow =
    "hidden";
}

function closeFreelance() {
  isDrawerOpen.value =
    false;

  document.body.style.overflow =
    "";

  window.setTimeout(
    () => {
      selectedFreelance.value =
        null;
    },
    200
  );
}

/* ========================================================= */
/* THEME */
/* ========================================================= */

function observeTheme() {
  themeObserver =
    new MutationObserver(
      () => {
        currentTheme.value =
          document.documentElement
            .getAttribute(
              "data-theme"
            ) ||
          "light";
      }
    );

  themeObserver.observe(
    document.documentElement,
    {
      attributes: true,
      attributeFilter: [
        "data-theme"
      ]
    }
  );
}

/* ========================================================= */
/* LIFECYCLE */
/* ========================================================= */

onMounted(() => {
  observeTheme();

  const auth = getAuth();

  authUnsubscribe =
    onAuthStateChanged(
      auth,
      async user => {
        if (!user) {
          console.warn(
            "⛔ Пользователь не авторизован"
          );

          freelances.value = [];
          isLoading.value = false;

          return;
        }

        await loadFreelances();
      }
    );

  window.addEventListener(
    "resize",
    updateMasonry
  );
});

watch(
  [
    isLoading,
    () => freelances.value.length
  ],
  async () => {
    await observeMasonryItems();
  },
  {
    flush: "post"
  }
);

onBeforeUnmount(() => {
  authUnsubscribe?.();

  resizeObserver?.disconnect();
  themeObserver?.disconnect();

  window.removeEventListener(
    "resize",
    updateMasonry
  );

  if (resizeFrame) {
    cancelAnimationFrame(
      resizeFrame
    );
  }

  document.body.style.overflow =
    "";
});
</script>

<style scoped>
* {
  box-sizing: border-box;
}

/* ========================================================= */
/* PAGE */
/* ========================================================= */

.page {
  min-height: 100vh;

  color:
    #252a31;

  background:
    linear-gradient(
      180deg,
      #f7f8fa 0%,
      #f3f5f7 100%
    );
}

.page.dark {
  color:
    #eceef1;

  background:
    linear-gradient(
      180deg,
      #191b1f 0%,
      #15171a 100%
    );
}

/* ========================================================= */
/* HEADER / FOOTER */
/* ========================================================= */

.fixed-header {
  position: fixed;

  top: 0;
  right: 0;
  left: 0;

  z-index: 1000;

  backdrop-filter:
    blur(16px);

  -webkit-backdrop-filter:
    blur(16px);
}

.fixed-footer {
  position: fixed;

  right: 0;
  bottom: 0;
  left: 0;

  z-index: 1000;

  backdrop-filter:
    blur(16px);

  -webkit-backdrop-filter:
    blur(16px);
}

/* ========================================================= */
/* HERO */
/* ========================================================= */

.hero {
  padding:
    88px
    24px
    0;
}

.hero-content {
  width: 1080px;
  max-width: 1120px;

  margin:
    0 auto;
}

.hero-card {
  position: relative;

  width: 100%;

  display: flex;
  align-items: center;
  justify-content: space-between;

  gap: 32px;

  padding:
    24px
    26px;

  overflow: hidden;

  border:
    1px solid
    rgba(30, 45, 65, 0.065);

  border-radius:
    22px;

  background:
    linear-gradient(
      145deg,
      rgba(255, 255, 255, 0.96),
      rgba(250, 251, 253, 0.96)
    );

  box-shadow:
    0 9px 26px
    rgba(20, 30, 45, 0.045);
}

.page.dark .hero-card {
  background:
    linear-gradient(
      145deg,
      #22242a 0%,
      #1d1f24 100%
    );

  border-color:
    rgba(255, 255, 255, 0.055);

  box-shadow:
    0 12px 28px
    rgba(0, 0, 0, 0.16);
}

.hero-copy {
  position: relative;
  z-index: 2;

  max-width: 470px;
}

.hero-eyebrow {
  display: inline-flex;
  align-items: center;

  gap: 7px;

  margin-bottom:
    10px;

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size: 8.5px;
  line-height: 1;

  letter-spacing:
    0.4px;

  text-transform:
    uppercase;

  color:
    #7d858f;
}

.hero-eyebrow-icon {
  width: 21px;
  height: 21px;

  display: flex;
  align-items: center;
  justify-content: center;

  border-radius:
    7px;

  background:
    rgba(40, 135, 247, 0.08);

  color:
    #2887f7;
}

.hero-eyebrow-icon svg {
  width: 13px;
  height: 13px;
}

.hero-title {
  margin: 0;

  font-family:
    "Eastman-Bold",
    sans-serif;

  font-size: 23px;
  font-weight: 700;
  line-height: 1.1;

  letter-spacing:
    -0.35px;

  color:
    #20242a;
}

.page.dark .hero-title {
  color:
    #f1f2f3;
}

.hero-subtitle {
  max-width: 450px;

  margin:
    9px 0 0;

  font-family:
    "Eastman-Regular",
    sans-serif;

  font-size: 10.5px;
  font-weight: 400;
  line-height: 1.5;

  letter-spacing: 0;

  color:
    #838a93;
}

.page.dark .hero-subtitle {
  color:
    #828892;
}

/* ========================================================= */
/* HERO META */
/* ========================================================= */

.hero-meta {
  position: relative;
  z-index: 2;

  width: 470px;
  max-width: 48%;

  display: flex;
  flex-direction: column;
  align-items: flex-start;

  gap: 9px;
}

.hero-meta-title {
  display: flex;
  align-items: center;

  gap: 6px;

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size: 8.5px;

  color:
    #8a9098;
}

.hero-meta-dot {
  width: 5px;
  height: 5px;

  border-radius: 50%;

  background:
    #2887f7;

  box-shadow:
    0 0 0 3px
    rgba(40, 135, 247, 0.09);
}

/* ========================================================= */
/* THEMES */
/* ========================================================= */

.themes {
  display: flex;
  flex-wrap: wrap;

  gap: 6px;
}

.theme-pill {
  min-height: 27px;

  display: inline-flex;
  align-items: center;

  gap: 6px;

  padding:
    0 9px;

  border:
    1px solid
    rgba(30, 45, 65, 0.055);

  border-radius:
    9px;

  background:
    #f6f7f9;

  color:
    #68717c;

  font-family:
    "Eastman-Regular",
    sans-serif;

  font-size: 9px;

  white-space:
    nowrap;
}

.page.dark .theme-pill {
  background:
    rgba(255, 255, 255, 0.04);

  border-color:
    rgba(255, 255, 255, 0.05);

  color:
    #9ca2aa;
}

.theme-dot {
  width: 5px;
  height: 5px;

  flex-shrink: 0;

  border-radius: 50%;
}

.theme-design .theme-dot {
  background:
    #27b782;
}

.theme-copywriting .theme-dot {
  background:
    #e6a23a;
}

.theme-marketing .theme-dot {
  background:
    #3d89e9;
}

.theme-video .theme-dot {
  background:
    #e76464;
}

.theme-other .theme-dot {
  background:
    #826ad0;
}

/* ========================================================= */
/* CONTACTS */
/* ========================================================= */

.contacts-row {
  min-height: 28px;

  display: flex;
  align-items: center;
  flex-wrap: wrap;

  gap: 5px;

  padding:
    0 9px;

  border:
    1px solid
    rgba(229, 147, 55, 0.08);

  border-radius:
    9px;

  background:
    rgba(229, 147, 55, 0.045);

  font-family:
    "Eastman-Regular",
    sans-serif;

  font-size: 8.5px;

  color:
    #7e858e;
}

.page.dark .contacts-row {
  background:
    rgba(229, 147, 55, 0.045);

  border-color:
    rgba(229, 147, 55, 0.07);

  color:
    #858b94;
}

.contacts-icon {
  width: 16px;
  height: 16px;

  display: flex;
  align-items: center;
  justify-content: center;

  color:
    #dc923e;
}

.contacts-icon svg {
  width: 13px;
  height: 13px;
}

.contacts-label {
  color:
    #666e78;
}

.page.dark .contacts-label {
  color:
    #aaaeb5;
}

.contacts-separator {
  opacity: 0.35;
}

/* ========================================================= */
/* DECORATION */
/* ========================================================= */

.hero-decoration {
  position: absolute;

  inset:
    0 0 0 auto;

  width: 280px;

  overflow: hidden;

  pointer-events: none;
}

.hero-decoration-circle {
  position: absolute;

  width: 190px;
  height: 190px;

  top: -95px;
  right: -45px;

  border-radius: 50%;

  background:
    radial-gradient(
      circle,
      rgba(40, 135, 247, 0.11) 0%,
      rgba(40, 135, 247, 0.02) 58%,
      transparent 72%
    );
}

.hero-decoration-circle-small {
  width: 130px;
  height: 130px;

  top: auto;
  right: 125px;
  bottom: -85px;

  background:
    radial-gradient(
      circle,
      rgba(126, 95, 220, 0.08) 0%,
      transparent 70%
    );
}

/* ========================================================= */
/* CONTENT */
/* ========================================================= */

.freelance-wrapper {
  width: 100%;
  max-width: 1120px;

  margin:
    0 auto;

  padding:
    28px
    24px
    110px;
}

.content-heading {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;

  gap: 20px;

  margin-bottom:
    16px;

  padding:
    0 2px;
}

.content-title {
  margin: 0;

  font-family:
    "Eastman-Bold",
    sans-serif;

  font-size: 15px;
  font-weight: 700;

  letter-spacing:
    -0.15px;

  color:
    #30353b;
}

.page.dark .content-title {
  color:
    #dedfe2;
}

.content-subtitle {
  margin:
    5px 0 0;

  font-family:
    "Eastman-Regular",
    sans-serif;

  font-size: 9px;
  line-height: 1.4;

  color:
    #949aa2;
}

.posts-count {
  min-width: 28px;
  height: 24px;

  display: inline-flex;
  align-items: center;
  justify-content: center;

  padding:
    0 8px;

  border:
    1px solid
    rgba(40, 135, 247, 0.08);

  border-radius:
    8px;

  background:
    rgba(40, 135, 247, 0.055);

  color:
    #397fcf;

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size: 9px;
}

.page.dark .posts-count {
  color:
    #7ab4f7;

  background:
    rgba(40, 135, 247, 0.08);

  border-color:
    rgba(80, 155, 255, 0.08);
}

/* ========================================================= */
/* MASONRY */
/* ========================================================= */

.freelance-grid {
  width: 100%;

  display: grid;

  grid-template-columns:
    repeat(
      3,
      minmax(0, 1fr)
    );

  grid-auto-rows:
    8px;

  column-gap:
    24px;

  row-gap:
    16px;

  align-items:
    start;
}

.masonry-item {
  min-width: 0;
  width: 100%;

  align-self:
    start;
}

.masonry-item > * {
  width: 100%;
}

/* ========================================================= */
/* EMPTY */
/* ========================================================= */

.freelance-grid.empty {
  display: block;
}

.empty-state {
  width: 100%;
  max-width: 480px;

  margin:
    40px auto 0;

  padding:
    34px 28px;

  text-align: center;

  border:
    1px solid
    rgba(30, 45, 65, 0.06);

  border-radius:
    20px;

  background:
    rgba(255, 255, 255, 0.88);

  box-shadow:
    0 8px 24px
    rgba(20, 30, 45, 0.04);
}

.page.dark .empty-state {
  background:
    #202227;

  border-color:
    rgba(255, 255, 255, 0.055);

  box-shadow:
    0 10px 28px
    rgba(0, 0, 0, 0.16);
}

.empty-icon {
  width: 48px;
  height: 48px;

  display: flex;
  align-items: center;
  justify-content: center;

  margin:
    0 auto 14px;

  border-radius:
    15px;

  background:
    rgba(40, 135, 247, 0.07);

  color:
    #2887f7;
}

.empty-icon svg {
  width: 23px;
  height: 23px;
}

.empty-title {
  margin: 0;

  font-family:
    "Eastman-Bold",
    sans-serif;

  font-size: 14px;

  color:
    #41464d;
}

.page.dark .empty-title {
  color:
    #d8dade;
}

.empty-text {
  max-width: 330px;

  margin:
    7px auto 0;

  font-family:
    "Eastman-Regular",
    sans-serif;

  font-size: 9.5px;
  line-height: 1.5;

  color:
    #949aa2;
}

/* ========================================================= */
/* DRAWER */
/* ========================================================= */

.drawer-backdrop {
  position: fixed;
  z-index: 900;

  inset: 0;

  background:
    rgba(8, 11, 16, 0.48);

  backdrop-filter:
    blur(6px);

  -webkit-backdrop-filter:
    blur(6px);
}

/* ========================================================= */
/* TRANSITION */
/* ========================================================= */

.fade-enter-active,
.fade-leave-active {
  transition:
    opacity 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

/* ========================================================= */
/* TABLET */
/* ========================================================= */

@media (max-width: 980px) {
  .hero-card {
    align-items:
      flex-start;

    flex-direction:
      column;

    gap: 20px;
  }

  .hero-meta {
    width: 100%;
    max-width: 100%;
  }

  .freelance-grid {
    grid-template-columns:
      repeat(
        2,
        minmax(0, 1fr)
      );
  }
}

/* ========================================================= */
/* MOBILE */
/* ========================================================= */

@media (max-width: 680px) {
  .hero {
    padding:
      78px
      14px
      0;
  }

  .hero-card {
    padding:
      20px 18px;

    border-radius:
      18px;
  }

  .hero-title {
    font-size: 20px;
  }

  .hero-subtitle {
    font-size: 10px;
  }

  .hero-meta {
    gap: 8px;
  }

  .themes {
    gap: 5px;
  }

  .theme-pill {
    min-height: 26px;

    padding:
      0 8px;

    font-size: 8.5px;
  }

  .contacts-row {
    font-size: 8px;
  }

  .freelance-wrapper {
    padding:
      22px
      14px
      105px;
  }

  .content-title {
    font-size: 14px;
  }

  .freelance-grid {
    grid-template-columns:
      minmax(0, 1fr);

    grid-auto-rows:
      auto;

    gap:
      18px;
  }

  .masonry-item {
    grid-row-end:
      auto !important;
  }
}

/* ========================================================= */
/* SMALL MOBILE */
/* ========================================================= */

@media (max-width: 390px) {
  .hero-title {
    font-size: 19px;
  }

  .contacts-row {
    line-height: 1.45;
  }
}

/* ========================================================= */
/* REDUCED MOTION */
/* ========================================================= */

@media (prefers-reduced-motion: reduce) {
  .fade-enter-active,
  .fade-leave-active {
    transition: none;
  }
}
</style>